import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Video, Plus, X, Eye, Download } from 'lucide-react';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import AdminForm from '../../components/admin/AdminForm';
import { galleryAPI } from '../../services/api';

const AdminGallery = () => {
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [editingGallery, setEditingGallery] = useState<any>(null);
  const [selectedGallery, setSelectedGallery] = useState<any>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | 'add'>('view');
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  const [showMediaViewModal, setShowMediaViewModal] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    fetchGalleries();
  }, [navigate]);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      const response = await galleryAPI.getAll({ active_only: false });
      if (response.records) {
        setGalleries(response.records);
      }
    } catch (error) {
      console.error('Error fetching galleries:', error);
      // Fallback data
      setGalleries([
        {
          id: 1,
          title_fr: 'Africa Surf Tour 2024 - Sénégal',
          title_en: 'Africa Surf Tour 2024 - Senegal',
          description_fr: 'Les meilleurs moments de l\'étape sénégalaise du tour',
          description_en: 'The best moments from the Senegalese stage of the tour',
          type: 'photos',
          collection: 'competitions',
          thumbnail_url: 'https://images.pexels.com/photos/1430672/pexels-photo-1430672.jpeg?auto=compress&cs=tinysrgb&w=600',
          media_count: 45,
          active: true,
          created_at: '2024-06-15T10:00:00Z'
        },
        {
          id: 2,
          title_fr: 'Highlights Taghazout',
          title_en: 'Highlights Taghazout',
          description_fr: 'Vidéos des sessions épiques à Taghazout',
          description_en: 'Videos of epic sessions in Taghazout',
          type: 'videos',
          collection: 'spots',
          thumbnail_url: 'https://images.pexels.com/photos/1761279/pexels-photo-1761279.jpeg?auto=compress&cs=tinysrgb&w=600',
          media_count: 12,
          active: true,
          created_at: '2024-06-10T10:00:00Z'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingGallery(null);
    setViewMode('add');
    setShowModal(true);
  };

  const handleEdit = (gallery: any) => {
    setEditingGallery(gallery);
    setViewMode('edit');
    setShowModal(true);
  };

  const handleView = (gallery: any) => {
    setEditingGallery(gallery);
    setViewMode('view');
    setShowModal(true);
  };

  const handleDelete = async (gallery: any) => {
    try {
      if (window.confirm(`Êtes-vous sûr de vouloir supprimer la galerie "${gallery.title_fr}" ?`)) {
        await galleryAPI.delete(gallery.id);
        setGalleries(prev => prev.filter(item => item.id !== gallery.id));
      }
    } catch (error) {
      console.error('Error deleting gallery:', error);
      alert('Erreur lors de la suppression de la galerie');
      
      // Simulate successful delete for demo purposes
      setGalleries(prev => prev.filter(item => item.id !== gallery.id));
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      setFormLoading(true);
      
      if (editingGallery) {
        // Update existing gallery
        const updatedData = { ...formData, id: editingGallery.id };
        await galleryAPI.update(updatedData);
        
        setGalleries(prev => prev.map(item => 
          item.id === editingGallery.id ? { ...item, ...formData } : item
        ));
      } else {
        // Add new gallery
        const response = await galleryAPI.create(formData);
        
        if (response.id) {
          const newGallery = {
            id: response.id,
            ...formData,
            media_count: 0,
            created_at: new Date().toISOString()
          };
          setGalleries(prev => [newGallery, ...prev]);
        } else {
          // Fallback for demo
          const newId = Math.max(0, ...galleries.map(item => item.id)) + 1;
          const newGallery = {
            id: newId,
            ...formData,
            media_count: 0,
            created_at: new Date().toISOString()
          };
          setGalleries(prev => [newGallery, ...prev]);
        }
      }
      
      setShowModal(false);
      setEditingGallery(null);
    } catch (error) {
      console.error('Error saving gallery:', error);
      alert('Erreur lors de l\'enregistrement de la galerie');
      
      // Simulate successful save for demo purposes
      if (editingGallery) {
        setGalleries(prev => prev.map(item => 
          item.id === editingGallery.id ? { ...item, ...formData } : item
        ));
      } else {
        const newId = Math.max(0, ...galleries.map(item => item.id)) + 1;
        const newGallery = {
          id: newId,
          ...formData,
          media_count: 0,
          created_at: new Date().toISOString()
        };
        setGalleries(prev => [newGallery, ...prev]);
      }
      
      setShowModal(false);
      setEditingGallery(null);
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewMedia = async (gallery: any) => {
    try {
      setLoading(true);
      const galleryDetails = await galleryAPI.getById(gallery.id);
      setSelectedGallery(galleryDetails);
      setShowMediaModal(true);
    } catch (error) {
      console.error('Error fetching gallery details:', error);
      
      // Fallback data for demo
      const mockMedia = Array.from({ length: gallery.media_count || 6 }, (_, i) => ({
        id: i + 1,
        gallery_id: gallery.id,
        title_fr: `${gallery.type === 'photos' ? 'Photo' : 'Vidéo'} ${i + 1}`,
        title_en: `${gallery.type === 'photos' ? 'Photo' : 'Video'} ${i + 1}`,
        media_url: gallery.type === 'photos' 
          ? `https://images.pexels.com/photos/${1400000 + i * 10000}/pexels-photo-${1400000 + i * 10000}.jpeg?auto=compress&cs=tinysrgb&w=1200`
          : `https://images.pexels.com/photos/${1600000 + i * 10000}/pexels-photo-${1600000 + i * 10000}.jpeg?auto=compress&cs=tinysrgb&w=1200`,
        media_type: gallery.type === 'photos' ? 'image' : 'video',
        order_position: i + 1
      })).slice(0, Math.min(gallery.media_count || 6, 6));
      
      setSelectedGallery({
        ...gallery,
        media: mockMedia
      });
      setShowMediaModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedia = async (formData: any) => {
    try {
      setFormLoading(true);
      
      if (selectedGallery) {
        await galleryAPI.addMedia(selectedGallery.id, formData);
        
        // Refresh gallery details
        const galleryDetails = await galleryAPI.getById(selectedGallery.id);
        setSelectedGallery(galleryDetails);
        
        // Update gallery count in the list
        setGalleries(prev => prev.map(item => 
          item.id === selectedGallery.id 
            ? { ...item, media_count: (item.media_count || 0) + 1 } 
            : item
        ));
      } else {
        // Fallback for demo
        const newMediaId = selectedGallery.media ? Math.max(0, ...selectedGallery.media.map((m: any) => m.id)) + 1 : 1;
        const newMedia = {
          id: newMediaId,
          gallery_id: selectedGallery.id,
          title_fr: formData.title_fr || '',
          title_en: formData.title_en || '',
          media_url: formData.media_url,
          media_type: formData.media_type,
          order_position: formData.order_position || 1
        };
        
        setSelectedGallery({
          ...selectedGallery,
          media: [...(selectedGallery.media || []), newMedia],
          media_count: ((selectedGallery.media || []).length || 0) + 1
        });
        
        setGalleries(prev => prev.map(item => 
          item.id === selectedGallery.id 
            ? { ...item, media_count: (item.media_count || 0) + 1 } 
            : item
        ));
      }
    } catch (error) {
      console.error('Error adding media:', error);
      alert('Erreur lors de l\'ajout du média');
      
      // Fallback for demo
      const newMediaId = selectedGallery.media ? Math.max(0, ...selectedGallery.media.map((m: any) => m.id)) + 1 : 1;
      const newMedia = {
        id: newMediaId,
        gallery_id: selectedGallery.id,
        title_fr: formData.title_fr || '',
        title_en: formData.title_en || '',
        media_url: formData.media_url,
        media_type: formData.media_type,
        order_position: formData.order_position || 1
      };
      
      setSelectedGallery({
        ...selectedGallery,
        media: [...(selectedGallery.media || []), newMedia],
        media_count: ((selectedGallery.media || []).length || 0) + 1
      });
      
      setGalleries(prev => prev.map(item => 
        item.id === selectedGallery.id 
          ? { ...item, media_count: (item.media_count || 0) + 1 } 
          : item
      ));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteMedia = async (mediaId: number) => {
    try {
      if (window.confirm('Êtes-vous sûr de vouloir supprimer ce média ?')) {
        await galleryAPI.deleteMedia(mediaId, selectedGallery.id);
        
        // Refresh gallery details
        const galleryDetails = await galleryAPI.getById(selectedGallery.id);
        setSelectedGallery(galleryDetails);
        
        // Update gallery count in the list
        setGalleries(prev => prev.map(item => 
          item.id === selectedGallery.id 
            ? { ...item, media_count: Math.max(0, (item.media_count || 0) - 1) } 
            : item
        ));
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Erreur lors de la suppression du média');
      
      // Fallback for demo
      setSelectedGallery({
        ...selectedGallery,
        media: selectedGallery.media.filter((m: any) => m.id !== mediaId),
        media_count: Math.max(0, (selectedGallery.media_count || 0) - 1)
      });
      
      setGalleries(prev => prev.map(item => 
        item.id === selectedGallery.id 
          ? { ...item, media_count: Math.max(0, (item.media_count || 0) - 1) } 
          : item
      ));
    }
  };

  const handleViewMediaItem = (media: any) => {
    setSelectedMedia(media);
    setShowMediaViewModal(true);
  };

  const formFields = [
    {
      name: 'title_fr',
      label: 'Titre (Français)',
      type: 'text' as const,
      required: true,
      placeholder: 'Titre de la galerie en français'
    },
    {
      name: 'title_en',
      label: 'Titre (Anglais)',
      type: 'text' as const,
      required: true,
      placeholder: 'Titre de la galerie en anglais'
    },
    {
      name: 'type',
      label: 'Type',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'photos', label: 'Photos' },
        { value: 'videos', label: 'Vidéos' }
      ]
    },
    {
      name: 'collection',
      label: 'Collection',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'competitions', label: 'Compétitions' },
        { value: 'spots', label: 'Spots de surf' },
        { value: 'athletes', label: 'Athlètes' },
        { value: 'documentaries', label: 'Documentaires' },
        { value: 'training', label: 'Formation' },
        { value: 'events', label: 'Événements' }
      ]
    },
    {
      name: 'description_fr',
      label: 'Description (Français)',
      type: 'textarea' as const,
      placeholder: 'Description de la galerie en français',
      rows: 3
    },
    {
      name: 'description_en',
      label: 'Description (Anglais)',
      type: 'textarea' as const,
      placeholder: 'Description de la galerie en anglais',
      rows: 3
    },
    {
      name: 'thumbnail_url',
      label: 'URL de la miniature',
      type: 'text' as const,
      placeholder: 'https://example.com/thumbnail.jpg'
    },
    {
      name: 'active',
      label: 'Active',
      type: 'checkbox' as const
    }
  ];

  const mediaFormFields = [
    {
      name: 'title_fr',
      label: 'Titre (Français)',
      type: 'text' as const,
      placeholder: 'Titre du média en français'
    },
    {
      name: 'title_en',
      label: 'Titre (Anglais)',
      type: 'text' as const,
      placeholder: 'Titre du média en anglais'
    },
    {
      name: 'media_url',
      label: 'URL du média',
      type: 'text' as const,
      required: true,
      placeholder: 'https://example.com/media.jpg'
    },
    {
      name: 'media_type',
      label: 'Type de média',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'image', label: 'Image' },
        { value: 'video', label: 'Vidéo' }
      ]
    },
    {
      name: 'order_position',
      label: 'Position',
      type: 'text' as const,
      placeholder: '1'
    }
  ];

  const columns = [
    {
      key: 'title_fr',
      label: 'Titre',
      sortable: true,
      render: (value: string, row: any) => (
        <div className="flex items-center">
          <div className={`p-2 rounded-lg mr-3 ${row.type === 'photos' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'}`}>
            {row.type === 'photos' ? <Image className="h-5 w-5" /> : <Video className="h-5 w-5" />}
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{row.title_en}</div>
          </div>
        </div>
      )
    },
    {
      key: 'collection',
      label: 'Collection',
      render: (value: string) => {
        const collections: {[key: string]: string} = {
          'competitions': 'Compétitions',
          'spots': 'Spots de surf',
          'athletes': 'Athlètes',
          'documentaries': 'Documentaires',
          'training': 'Formation',
          'events': 'Événements'
        };
        
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
            {collections[value] || value}
          </span>
        );
      }
    },
    {
      key: 'type',
      label: 'Type',
      render: (value: string) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'photos' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
        }`}>
          {value === 'photos' ? 'Photos' : 'Vidéos'}
        </span>
      )
    },
    {
      key: 'media_count',
      label: 'Médias',
      sortable: true
    },
    {
      key: 'active',
      label: 'Statut',
      render: (value: boolean) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR')
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des galeries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <AdminTable
          title="Gestion des Galeries"
          data={galleries}
          columns={columns}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleViewMedia}
          searchable
          filterable
          selectable
          pagination
          itemsPerPage={10}
        />

        {/* Gallery Form Modal */}
        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingGallery(null);
          }}
          title={
            viewMode === 'add' 
              ? 'Nouvelle galerie' 
              : viewMode === 'edit' 
                ? 'Modifier la galerie' 
                : 'Détails de la galerie'
          }
          size="lg"
        >
          {viewMode === 'view' ? (
            <div className="space-y-6">
              <div className="flex items-center space-x-6 mb-6">
                <div className={`p-4 rounded-lg ${
                  editingGallery?.type === 'photos' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'
                }`}>
                  {editingGallery?.type === 'photos' ? <Image className="h-8 w-8" /> : <Video className="h-8 w-8" />}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{editingGallery?.title_fr}</h3>
                  <div className="text-gray-500">{editingGallery?.title_en}</div>
                </div>
              </div>
              
              {editingGallery?.thumbnail_url && (
                <div className="mb-6">
                  <img 
                    src={editingGallery.thumbnail_url} 
                    alt={editingGallery.title_fr} 
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Type</h3>
                  <p className="text-gray-800">
                    {editingGallery?.type === 'photos' ? 'Photos' : 'Vidéos'}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Collection</h3>
                  <p className="text-gray-800">
                    {editingGallery?.collection === 'competitions' ? 'Compétitions' :
                     editingGallery?.collection === 'spots' ? 'Spots de surf' :
                     editingGallery?.collection === 'athletes' ? 'Athlètes' :
                     editingGallery?.collection === 'documentaries' ? 'Documentaires' :
                     editingGallery?.collection === 'training' ? 'Formation' :
                     editingGallery?.collection === 'events' ? 'Événements' :
                     editingGallery?.collection}
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Description (Français)</h3>
                <p className="text-gray-800 bg-gray-50 p-4 rounded-lg">{editingGallery?.description_fr}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Description (Anglais)</h3>
                <p className="text-gray-800 bg-gray-50 p-4 rounded-lg">{editingGallery?.description_en}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nombre de médias</h3>
                  <p className="text-gray-800">{editingGallery?.media_count || 0}</p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Statut</h3>
                  <p className="text-gray-800">
                    {editingGallery?.active ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => handleViewMedia(editingGallery)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>Voir les médias</span>
                </button>
                <button
                  onClick={() => {
                    setViewMode('edit');
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingGallery(null);
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            <AdminForm
              fields={formFields}
              initialData={editingGallery || { active: true, type: 'photos', collection: 'competitions' }}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowModal(false);
                setEditingGallery(null);
              }}
              loading={formLoading}
            />
          )}
        </AdminModal>

        {/* Media Manager Modal */}
        <AdminModal
          isOpen={showMediaModal}
          onClose={() => {
            setShowMediaModal(false);
            setSelectedGallery(null);
          }}
          title={selectedGallery ? `Médias - ${selectedGallery.title_fr}` : 'Médias'}
          size="xl"
        >
          {selectedGallery && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ajouter un média</h3>
                <AdminForm
                  fields={mediaFormFields}
                  initialData={{ 
                    media_type: selectedGallery.type === 'photos' ? 'image' : 'video',
                    order_position: selectedGallery.media ? selectedGallery.media.length + 1 : 1
                  }}
                  onSubmit={handleAddMedia}
                  onCancel={() => {}}
                  loading={formLoading}
                  submitText="Ajouter"
                  cancelText=""
                />
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Médias existants ({selectedGallery.media?.length || 0})</h3>
                
                {selectedGallery.media && selectedGallery.media.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedGallery.media.map((media: any) => (
                      <div key={media.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="relative h-40">
                          <img 
                            src={media.media_url}
                            alt={media.title_fr || media.title_en || 'Media'}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                            <button 
                              onClick={() => handleViewMediaItem(media)}
                              className="bg-white p-2 rounded-full mr-2"
                            >
                              <Eye className="h-4 w-4 text-blue-600" />
                            </button>
                            <button 
                              onClick={() => handleDeleteMedia(media.id)}
                              className="bg-white p-2 rounded-full"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </button>
                          </div>
                          {media.media_type === 'video' && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Video className="h-10 w-10 text-white bg-black/50 p-2 rounded-full" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="font-medium text-gray-900 truncate">
                            {media.title_fr || media.title_en || `Média #${media.id}`}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-sm text-gray-500">
                              Position: {media.order_position}
                            </p>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              media.media_type === 'image' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {media.media_type === 'image' ? 'Image' : 'Vidéo'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                    <Image className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">Aucun média dans cette galerie</p>
                    <p className="text-sm text-gray-400 mt-1">Ajoutez des médias en utilisant le formulaire ci-dessus</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </AdminModal>

        {/* Media View Modal */}
        <AdminModal
          isOpen={showMediaViewModal}
          onClose={() => {
            setShowMediaViewModal(false);
            setSelectedMedia(null);
          }}
          title={selectedMedia ? (selectedMedia.title_fr || selectedMedia.title_en || 'Aperçu du média') : 'Aperçu du média'}
          size="xl"
        >
          {selectedMedia && (
            <div className="text-center">
              <div className="mb-4 max-h-[70vh] overflow-hidden rounded-lg">
                {selectedMedia.media_type === 'image' ? (
                  <img 
                    src={selectedMedia.media_url}
                    alt={selectedMedia.title_fr || selectedMedia.title_en || 'Media'}
                    className="max-w-full max-h-[70vh] mx-auto object-contain"
                  />
                ) : (
                  <div className="relative bg-black aspect-video flex items-center justify-center">
                    <Video className="h-20 w-20 text-white/50" />
                    <p className="absolute bottom-4 text-white text-sm">
                      Aperçu vidéo non disponible - URL: {selectedMedia.media_url}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-center space-x-4 mt-4">
                <a 
                  href={selectedMedia.media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>Voir l'original</span>
                </a>
                <a 
                  href={selectedMedia.media_url}
                  download
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Télécharger</span>
                </a>
              </div>
              
              <div className="mt-6 text-left">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Titre (Français)</h3>
                    <p className="text-gray-900">{selectedMedia.title_fr || '-'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Titre (Anglais)</h3>
                    <p className="text-gray-900">{selectedMedia.title_en || '-'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Type</h3>
                    <p className="text-gray-900">{selectedMedia.media_type === 'image' ? 'Image' : 'Vidéo'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Position</h3>
                    <p className="text-gray-900">{selectedMedia.order_position}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </AdminModal>
      </div>
    </div>
  );
};

export default AdminGallery;